/**
 * ringStatus — THE binary avatar-ring predicate (Skip ruling 2026-07-08,
 * DEV-1437).
 *
 * The ring is BINARY and earned by ACTS, never conferred by title:
 *   WHITE — present but passive: the rung basics are not (currently) done.
 *   BLUE (Enabler blue) — actively enabling: rung basics done — complete
 *   profile + ≥3 follows + CURRENT EULA + CURRENT community standards.
 *
 * Consequences locked with the ruling:
 *   - Appointed Champions/Governors start WHITE until they do the basics.
 *   - Rungs are NEVER color-coded on the ring — medals carry progression
 *     (DEV-1301). One transition per user: white → blue (the promotion
 *     animation is that crossing, once).
 *   - CURRENCY is live: an EULA or standards version bump turns the ring
 *     white until re-acceptance — blue always means "in good standing on
 *     the basics right now", matching the voice-ladder posting gate.
 *
 * This mirrors OnboardingRung.RungBasicsCompleteAsync (server). Keep them
 * in lockstep — if one changes, change both. Deliberate simplification vs
 * the server: the joined-community clause is omitted client-side (a home
 * declaration is REQUIRED at signup, so every real account passes it; the
 * server gate still enforces it where it matters).
 *
 * Sysadmin exception: platform stewards render blue regardless — the
 * pre-ruling behavior of the profile swoosh/topbar arc, kept so admin
 * accounts (which may predate onboarding profiles) don't churn. Strike it
 * if Skip rules otherwise.
 *
 * Accepts both the snake_case seed shape and the camelCase
 * SecurityUserResponse so every surface can call it directly.
 */

import { EULA_CURRENT_VERSION } from '../../content/legal/eulaConstants';
import { STANDARDS_CURRENT_VERSION } from '../../content/legal/standardsConstants';
import { profileCompletion } from './profileCompletion';

export function hasBlueRing(user: Record<string, any> | null | undefined): boolean {
  if (!user) return false;

  if (user.is_system_admin || user.isSystemAdmin) return true;

  // §7.17 (DEV-1484): the singular predicate now includes the COVER photo
  // (profileCompletion helper — one definition everywhere). Live-currency
  // consequence, same semantics as an EULA version bump: a blue ring
  // earned under the pre-cover predicate flips white until the cover is
  // added. Flagged in the DEV-1484 status for the architect.
  const displayName = user.display_name ?? user.displayName;
  const profileComplete =
    !!String(displayName || '').trim() && profileCompletion(user).complete;

  const eulaVersion = user.eula_version_accepted ?? user.eulaVersionAccepted ?? 0;
  const eulaAt = user.eula_accepted_at ?? user.eulaAcceptedAt;
  const eulaCurrent = !!eulaAt && eulaVersion >= EULA_CURRENT_VERSION;

  const stdVersion =
    user.community_standards_version_accepted ?? user.communityStandardsVersionAccepted ?? 0;
  const stdAt = user.community_standards_accepted_at ?? user.communityStandardsAcceptedAt;
  const standardsCurrent = !!stdAt && stdVersion >= STANDARDS_CURRENT_VERSION;

  const followCount = user.follow_count ?? user.followCount ?? 0;

  return profileComplete && eulaCurrent && standardsCurrent && followCount >= 3;
}
